import type { TFunction } from 'i18next';

import { formatCurrency } from '@/lib/utils';
import type {
  Action,
  Condition,
  Names,
  Trigger,
  TriggerEvent,
  WorkflowDefinition,
} from './workflows-api';
import { PLACEHOLDERS } from './workflows-api';

const PLACEHOLDER_KEYS = new Set(Object.keys(PLACEHOLDERS));

/* ---------------------------------------------------------------------------
   התהליך במילים: "כאשר נחתמת הצעת מחיר, ובתנאי שסוג העבודה הוא מטבח — אז
   תיפתח משימה "מדידה" ל-2 ימים ומשובצת לאבי." משמש בבונה, ברשימה ובכרטיס
   של העוזר. שמות (סוג עבודה, עובד, ספק) מגיעים מ-names; מזהה לא מוכר → "?".
   --------------------------------------------------------------------------- */

type T = TFunction<'workflows'>;

/** "quote.approved" → מפתח תרגום (נקודה היא מפריד מפתחות ב-i18next). */
export const eventKey = (event: TriggerEvent) =>
  `trigger.event.${event.replace('.', '_') as EventKeyPart}` as const;
type EventKeyPart =
  | 'quote_sent'
  | 'quote_approved'
  | 'quote_declined'
  | 'quote_expired'
  | 'invoice_issued'
  | 'task_created'
  | 'task_assigned'
  | 'task_closed'
  | 'customer_created'
  | 'supplier_invoice_received';

const EMPTY: Names = { jobTypes: {}, users: {}, suppliers: {} };

function nameList(t: T, ids: string[] | undefined, map: Record<string, string>): string {
  return (ids ?? []).map((id) => map[id] ?? t('sentence.unknown')).join(t('sentence.or'));
}

export function describeTrigger(t: T, trigger: Trigger): string {
  switch (trigger.type) {
    case 'event': {
      const event = t(eventKey(trigger.event));
      const days = trigger.delayDays ?? 0;
      return days > 0
        ? t('sentence.delayed', { count: days, event })
        : t('sentence.event', { event });
    }
    case 'daily':
      return t('sentence.daily', { hour: `${String(trigger.hour).padStart(2, '0')}:00` });
    case 'contract_notice':
      return t('sentence.contract', { count: trigger.daysBefore });
  }
}

export function describeCondition(t: T, c: Condition, names: Names = EMPTY): string {
  switch (c.type) {
    case 'job_type': {
      const list = nameList(t, c.jobTypeIds, names.jobTypes);
      return c.op === 'in'
        ? t('sentence.jobTypeIn', { names: list })
        : t('sentence.jobTypeNotIn', { names: list });
    }
    case 'amount': {
      const amount = formatCurrency(c.value);
      return c.op === 'gte'
        ? t('sentence.amountGte', { amount })
        : t('sentence.amountLte', { amount });
    }
    case 'customer_kind':
      return c.value === 'new' ? t('sentence.customerNew') : t('sentence.customerReturning');
    case 'task_priority':
      return t('sentence.priority', {
        list: c.priorities
          .map((p) => t(`condition.priority.${String(p) as '1' | '2' | '3'}`))
          .join(t('sentence.or')),
      });
    case 'assignee':
      return c.op === 'none'
        ? t('sentence.assigneeNone')
        : t('sentence.assigneeIn', { names: nameList(t, c.userIds, names.users) });
    case 'task_source':
      return t('sentence.source', {
        list: c.sources.map((s) => t(`condition.source.${s}`)).join(t('sentence.or')),
      });
    case 'task_field':
      return t('sentence.field', { key: c.key, value: c.value });
    case 'contract_category':
      return t('sentence.contractCategory', { list: c.categories.join(t('sentence.or')) });
  }
}

/** "{{customer.name}}" → "‹שם הלקוח›" — המשפט נקרא כמו טקסט, לא כמו קוד. */
export function humanize(t: T, text: string): string {
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, key: string) =>
    PLACEHOLDER_KEYS.has(key) ? `‹${t(`placeholder.${key.replace('.', '_')}` as 'placeholder.today')}›` : '',
  );
}

export function describeAction(t: T, a: Action, names: Names = EMPTY): string {
  switch (a.type) {
    case 'create_task': {
      let s = t('sentence.createTask', { title: humanize(t, a.title) });
      if (a.jobTypeId)
        s += t('sentence.ofType', { name: names.jobTypes[a.jobTypeId] ?? t('sentence.unknown') });
      if (a.dueInDays !== undefined)
        s += a.dueInDays === 0 ? t('sentence.dueToday') : t('sentence.due', { count: a.dueInDays });
      const assign = a.assign ?? { mode: 'none' };
      if (assign.mode === 'user')
        s += t('sentence.assignTo', { name: names.users[assign.userId] ?? t('sentence.unknown') });
      if (assign.mode === 'auto') s += t('sentence.assignAuto');
      return s;
    }
    case 'add_checklist_items':
      return t('sentence.checklist', { items: a.items.join(', ') });
    case 'notify_team': {
      const who = [
        ...((a.includeOwners ?? true) ? [t('sentence.owners')] : []),
        ...(a.userIds ?? []).map((id) => names.users[id] ?? t('sentence.unknown')),
      ].join(t('sentence.and'));
      return t('sentence.notify', { who }) + (a.email ? t('sentence.notifyEmail') : '');
    }
    case 'send_customer_message':
      return t('sentence.message', { template: t(`action.template.${a.template}`) });
    case 'reminder':
      return t('sentence.reminder', { count: a.inDays, message: humanize(t, a.message) });
    case 'set_task_status':
      return t('sentence.status');
    case 'purchase_order_draft':
      return t('sentence.po', { supplier: names.suppliers[a.supplierId] ?? t('sentence.unknown') });
  }
}

/** המשפט המלא. */
export function describeWorkflow(
  t: T,
  def: Pick<WorkflowDefinition, 'trigger' | 'conditions' | 'actions'>,
  names: Names = EMPTY,
): string {
  const when = describeTrigger(t, def.trigger);
  const conditions = def.conditions.map((c) => describeCondition(t, c, names));
  const actions = def.actions.map((a) => describeAction(t, a, names));
  return (
    when +
    (conditions.length
      ? t('sentence.if', { conditions: conditions.join(t('sentence.and')) })
      : '') +
    t('sentence.then', { actions: actions.join(t('sentence.and')) })
  );
}
